/**
 * @fileoverview HTTP client with interceptors for API communication
 * @module services/api
 */

import Swal from 'sweetalert2';
import th from '../components/language/th.json';
import en from '../components/language/en.json';
import { translateMessage, translateTitle } from '../utils/backendMessage';
import { clearStorageKeepLang } from '../utils/backendMessage';

// แปลข้อความปุ่มตามภาษาปัจจุบัน — ใช้ logic เดียวกับ `t()` ใน components/CustomComponent.js
// (คัดลอกมาแทนที่จะ import ตรง เพื่อกัน circular import: CustomComponent.js import GET/POST ฯลฯ จากไฟล์นี้อยู่แล้ว)
const translate = (key) => {
  const currentLang = localStorage.getItem('lang') || 'TH';
  const translations = currentLang === 'EN' ? en : th;
  return translations[key] || key;
};

/**
 * Get authentication token from localStorage
 * @returns {string} Decoded token
 */
const getAuthToken = () => {
  const tokens = localStorage.getItem('cm-acc-tk') || '';
  try {
    return tokens ? atob(tokens) : '';
  } catch {
    // ค่าใน storage เสีย: ถือว่ายังไม่ได้ล็อกอิน
    return '';
  }
};
const getAuthCompany = () => {
  let company = localStorage.getItem('cm-company') || '';
  try {
    company = company ? JSON.parse(company) : '';
  } catch {
    company = '';
  }
  return company?.id || '';
};

/**
 * Show alert dialog using SweetAlert2
 * @param {string} url - URL to redirect after alert
 * @param {string} icon - Alert icon type
 * @param {string} title - Alert title
 * @param {string} detail - Alert message
 * @param {string} confirm - Confirm button text
 * @param {string} cancel - Cancel button text
 * @returns {Promise}
 */
export const showAlert = (url, icon, title, detail, confirm, cancel) => {
  const modals = document.getElementsByClassName('swal2-modal');
  const icons = document.getElementsByClassName('swal2-icon');
  const hasBangIcon = Array.from(icons).some((el) => el.textContent.trim() === '!');

  if (modals.length >= 2 || hasBangIcon) return;

  return Swal.fire({
    icon: icon || 'warning',
    title: translateTitle(title || (icon === 'success' ? 'Success' : 'Warning')),
    text: translateMessage(detail || (icon === 'success' ? 'The transaction was completed successfully.' : ' ')),
    confirmButtonText: translateMessage(confirm) || (cancel ? translate('confirm') : translate('ok')),
    showCancelButton: !!cancel,
    cancelButtonText: translateMessage(cancel) || translate('cancel'),
    background: '#fff',
    color: '#000000',
    confirmButtonColor: '#212529',
    cancelButtonColor: 'unset',
  }).then(({ isConfirmed }) => {
    if (url) {
      if (url === '#') window.location.reload();
      else window.location.href = url;
    }
    return isConfirmed;
  });
};

/**
 * Show/hide loading spinner
 * @param {boolean} status - Show or hide loading
 */
export const loading = (status) => {
  try {
    const loadingEl = document.getElementById('loading');
    if (status === true) {
      loadingEl.classList.remove('d-none');
    } else if (status === false) {
      loadingEl.classList.add('d-none');
    }
  } catch (e) {}
};

/**
 * Remove empty strings and null values from objects/arrays recursively
 * @param {*} value - The value to clean
 * @returns {*} Cleaned value
 */
// export const removeEmptyString = (value) => {
//   if (Array.isArray(value)) {
//     const arr = value.map(removeEmptyString).filter((v) => v !== '' && v !== null);
//     return arr.length > 0 ? arr : null;
//   }

//   if (value && typeof value === 'object') {
//     const obj = Object.fromEntries(
//       Object.entries(value)
//         .map(([k, v]) => [k, removeEmptyString(v)])
//         .filter(([_, v]) => v !== '' && v !== null)
//     );
//     return Object.keys(obj).length > 0 ? obj : null;
//   }

//   return value;
// };
export const removeEmptyString = (value) => {
  // ⭐ แปลง "-" เป็น "" แต่ไม่ให้ถูกลบ
  if (value === '-') {
    return '__EMPTY__'; // ใช้ placeholder กันโดน filter
  }

  if (Array.isArray(value)) {
    const arr = value
      .map(removeEmptyString)
      .filter((v) => v !== '' && v !== null);
    
    return arr.map(v => v === '__EMPTY__' ? '' : v);
  }

  if (value && typeof value === 'object') {
    const obj = Object.fromEntries(
      Object.entries(value)
        .map(([k, v]) => [k, removeEmptyString(v)])
        .filter(([_, v]) => v !== '' && v !== null)
    );

    // แปลง placeholder กลับ
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [
        k,
        v === '__EMPTY__' ? '' : v
      ])
    );
  }

  return value;
};

/**
 * Extract error message from API response
 * @param {Object} result - API response
 * @returns {string} Error message
 */
export const ERROR = (result) => {
  loading(false);
  if (!result) return '';
  if (result.detail) return result.detail;
  if (result.message) return result.message;
  if (result.data) {
    if (result.data.message) return result.data.message;
    return result.data;
  }
  return result.message || '';
};

/**
 * Build API URL
 * @param {string} url - Endpoint URL
 * @param {Object} params - Query parameters
 * @returns {string} Full URL
 */
const buildUrl = (url, params = null) => {
  let fullUrl = url.includes('://') ? url : (process.env.REACT_APP_API_URL + url).replaceAll(' ', '');

  if (params) {
    const queryString = Object.entries(params)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    fullUrl += '?' + queryString;
  }

  return fullUrl;
};

/**
 * Handle API errors
 * @param {Object} result - API response
 * @param {Object} options - Options
 * @returns {Object} Processed response
 */
const handleResponse = (result, options = {}) => {
  if (result.status === true) return result;

  const message = ERROR(result);

  // งานเบื้องหลัง (เช่น poll ป้ายแจ้งเตือน) ไม่ต้องเด้ง alert รบกวนผู้ใช้
  if (options.silent) return { status: false, message };

  // 409 optimistic lock: backend ส่ง errors.code = "STALE_DATA" เมื่อข้อมูลใน DB ใหม่กว่าที่หน้าถืออยู่
  // ไม่เด้ง alert ทั่วไปที่นี่ ให้หน้าที่เรียกเป็นคนแจ้งผู้ใช้เอง แล้วโหลดข้อมูลใหม่ (ดู work-orders.js handleStaleWorkOrder)
  // 409 TECHNICIAN_CODE_EXISTS: รหัสช่างซ้ำกับช่างที่ใช้งานอยู่ — หน้าช่างถามยืนยันอัปเดตเอง ไม่เด้ง alert ทั่วไป
  if (result?.errors?.code === 'TECHNICIAN_CODE_EXISTS') {
    return { status: false, message, errors: result.errors };
  }

  // 409 PL_STOCK_CHANGED (ใบขอเบิกอะไหล่): สต็อกเปลี่ยนตอนบันทึก — modal ใบขอเบิกแสดงแบนเนอร์รายการที่ไม่พอเอง ไม่เด้ง alert ซ้ำ
  if (result?.errors?.code === 'PL_STOCK_CHANGED') {
    return { status: false, message, errors: result.errors };
  }

  // 409 WBS_ISSUE_STOCK_CHANGED (E4) / WBS_ISSUE_RETRY_STOCK_SHORT (E12) (เอกสารเบิกอะไหล่ WBS): แผงเอกสารเบิกแสดงแบนเนอร์/กล่องแจ้งรายการที่ไม่พอเอง ไม่เด้ง alert ซ้ำ
  if (result?.errors?.code === 'WBS_ISSUE_STOCK_CHANGED' || result?.errors?.code === 'WBS_ISSUE_RETRY_STOCK_SHORT') {
    return { status: false, message, errors: result.errors };
  }

  if (result?.errors?.code === 'STALE_DATA') {
    return { status: false, message, errors: result.errors };
  }

  if (message === 'Token not found' || message === 'Token has expired') {
    clearStorageKeepLang();
    showAlert('/', 'warning', 'Warning', message);
    return { status: false, message };
  }

  // บัญชีถูกบังคับให้เปลี่ยนรหัสผ่านก่อน (must_change_password = true) - backend ตอบ 403 นี้ให้ endpoint อื่นทุกตัว
  // ไม่ล้าง session เพราะ token ยังใช้ auth/change-password ได้อยู่ (ดู listAllowedWhenMustChangePassword ฝั่ง backend)
  if (message === 'Password change required') {
    if (window.location.pathname !== '/change-password') {
      window.location.href = '/change-password';
    }
    return { status: false, message };
  }

  if (options.skipUserVerificationAlert && message === 'User not verified, please verify your account first') {
    return { status: false, message };
  }

  // backend GB_PERMISSION_FAIL = "Access denied" -> แสดงข้อความตามภาษาที่เลือก (ทุกหน้า/ทุกปุ่มที่ตรวจสิทธิ์)
  if (String(message).toLowerCase() === 'access denied') {
    if (!options.skipAccessDeniedAlert) {
      // เรียก Swal ตรงโดยไม่ผ่าน guard ของ showAlert (swal ยืนยันที่กำลังปิดทำให้ guard ข้าม alert นี้) ปิดของเดิมก่อนแล้วค่อยแสดง
      Swal.close();
      Swal.fire({
        icon: 'warning',
        title: translate('access_denied_title'),
        text: translate('access_denied_msg'),
        confirmButtonText: translate('ok'),
        background: '#fff',
        color: '#000000',
        confirmButtonColor: '#212529',
      });
    }
    return { status: false, message };
  }

  if (message === 'fetch is not defined') {
    showAlert('', 'warning', 'Warning', 'Data not found');
    return { status: false, message };
  }

  showAlert('', 'warning', 'Warning', message);
  return { status: false, message };
};

/**
 * Perform GET request
 * @param {string} url - API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} API response
 */
export const GET = async (url, params, company_uid = true, options = {}) => {
  return new Promise((resolve) => {
    const apiUrl = buildUrl(url,params);
    const companyUid = getAuthCompany(); // ดึงค่ามาก่อน

    // สร้าง Header พื้นฐาน
    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: getAuthToken(),
    };

    // ตรวจสอบว่าถ้ามีค่าค่อยใส่เข้าไป (ถ้าเป็น null/undefined/ว่าง จะไม่ใส่)
    if (companyUid && company_uid) {
      headers['company-uid'] = companyUid;
    }

    fetch(apiUrl, {
      method: 'GET',
      headers: headers, // ใช้ header ที่เตรียมไว้
      cache: 'no-cache',
    })
      .then((result) => result.json())
      .then((result) => resolve(handleResponse(result, options)))
      .catch(() => resolve({ status: false, message: 'Error' }));
  });
};

/**
 * ดึงไฟล์ไบนารี (เช่น PDF ที่ backend เรนเดอร์มาให้) - GET ปกติ parse เป็น JSON จึงใช้กับไฟล์ไม่ได้
 * คืน Blob เพื่อเอาไปเปิดแท็บใหม่หรือสั่งดาวน์โหลดต่อ
 * @param {string} url
 * @returns {Promise<{status: boolean, blob?: Blob, fileName?: string, message?: string}>}
 */
// อ่านชื่อไฟล์จาก header Content-Disposition (คืน '' ถ้าไม่มี) - backend เป็นคนตั้งชื่อไฟล์ Service Report
const parseContentDispositionFileName = (header) => {
  if (!header) return '';
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star) {
    try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')); } catch (e) { /* ใช้ filename ธรรมดาแทน */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : '';
};

export const GET_FILE = async (url, params, company_uid = true) => {
  const apiUrl = buildUrl(url, params);
  const companyUid = getAuthCompany();
  const headers = { Authorization: getAuthToken() };
  if (companyUid && company_uid) headers['company-uid'] = companyUid;

  try {
    const res = await fetch(apiUrl, { method: 'GET', headers, cache: 'no-cache' });
    if (!res.ok) {
      // ฝั่ง error backend ตอบเป็น JSON ปกติ ดึงข้อความจริงมาแสดงแทนข้อความรวม ๆ
      let message = `HTTP ${res.status}`;
      try {
        const body = await res.json();
        message = body?.message || message;
      } catch (e) {
        // ไม่ใช่ JSON ก็ใช้สถานะ HTTP ไปตามเดิม
      }
      return { status: false, message };
    }
    return { status: true, blob: await res.blob(), fileName: parseContentDispositionFileName(res.headers.get('Content-Disposition')) };
  } catch (e) {
    return { status: false, message: 'Error' };
  }
};

/**
 * Perform POST request
 * @param {string} url - API endpoint
 * @param {Object} body - Request body
 * @returns {Promise<Object>} API response
 */
export const POST = async (url, body, company_uid = true) => {
  return new Promise((resolve) => {
    const apiUrl = buildUrl(url);
    const companyUid = getAuthCompany(); // ดึงค่ามาก่อน

    // สร้าง Header พื้นฐาน
    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: getAuthToken(),
    };

    // ตรวจสอบว่าถ้ามีค่าค่อยใส่เข้าไป (ถ้าเป็น null/undefined/ว่าง จะไม่ใส่)
    if (companyUid && company_uid) {
      headers['company-uid'] = companyUid;
    }

    fetch(apiUrl, {
      method: 'POST',
      headers: headers, // ใช้ header ที่เตรียมไว้
      cache: 'no-cache',
      body: JSON.stringify(removeEmptyString(body)),
    })
      .then((result) => result.json())
      .then((result) => resolve(handleResponse(result, { skipUserVerificationAlert: true })))
      .catch(() => resolve({ status: false, message: 'Error' }));
  });
};

/**
 * Perform PUT request
 * @param {string} url - API endpoint
 * @param {Object} body - Request body
 * @returns {Promise<Object>} API response
 */
export const PUT = async (url, body, company_uid = true) => {
  return new Promise((resolve) => {
    const apiUrl = buildUrl(url);
    const companyUid = getAuthCompany(); // ดึงค่ามาก่อน

    // สร้าง Header พื้นฐาน
    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: getAuthToken(),
    };

    // ตรวจสอบว่าถ้ามีค่าค่อยใส่เข้าไป (ถ้าเป็น null/undefined/ว่าง จะไม่ใส่)
    if (companyUid && company_uid) {
      headers['company-uid'] = companyUid;
    }

    fetch(apiUrl, {
      method: 'PUT',
        headers: headers, // ใช้ header ที่เตรียมไว้
      cache: 'no-cache',
      body: JSON.stringify(removeEmptyString(body)),
    })
      .then((result) => result.json())
      .then((result) => resolve(handleResponse(result)))
      .catch(() => resolve({ status: false, message: 'Error' }));
  });
};

/**
 * Perform PATCH request
 * @param {string} url - API endpoint
 * @param {Object} body - Request body
 * @returns {Promise<Object>} API response
 */
export const PATCH = async (url, body, company_uid = true) => {
  return new Promise((resolve) => {
    const apiUrl = buildUrl(url);
    const companyUid = getAuthCompany();

    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: getAuthToken(),
    };

    if (companyUid && company_uid) {
      headers['company-uid'] = companyUid;
    }

    fetch(apiUrl, {
      method: 'PATCH',
      headers,
      cache: 'no-cache',
      body: JSON.stringify(removeEmptyString(body)),
    })
      .then((result) => result.json())
      .then((result) => resolve(handleResponse(result)))
      .catch(() => resolve({ status: false, message: 'Error' }));
  });
};

/**
 * Perform DELETE request
 * @param {string} url - API endpoint
 * @param {Object} body - Request body
 * @returns {Promise<Object>} API response
 */
export const DELETE = async (url, body, company_uid = true) => {
  return new Promise((resolve) => {
    const apiUrl = buildUrl(url);
    const companyUid = getAuthCompany(); // ดึงค่ามาก่อน

    // สร้าง Header พื้นฐาน
    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: getAuthToken(),
    };

    // ตรวจสอบว่าถ้ามีค่าค่อยใส่เข้าไป (ถ้าเป็น null/undefined/ว่าง จะไม่ใส่)
    if (companyUid && company_uid) {
      headers['company-uid'] = companyUid;
    }

    fetch(apiUrl, {
      method: 'DELETE',
      headers: headers, // ใช้ header ที่เตรียมไว้
      cache: 'no-cache',
    })
      .then((result) => result.json())
      .then((result) => resolve(handleResponse(result)))
      .catch(() => resolve({ status: false, message: 'Error' }));
  });
};

/**
 * ขนาดไฟล์สูงสุดที่อนุญาตให้อัปโหลดฝั่ง client (หน่วย: byte)
 * ใช้ตรวจก่อนอัปโหลดเพื่อกัน error 413 และให้ผู้ใช้เห็น message ที่เข้าใจง่าย
 */
export const MAX_UPLOAD_SIZE = {
  image: 15 * 1024 * 1024, // 15MB
  video: 100 * 1024 * 1024, // 100MB
  default: 50 * 1024 * 1024, // 50MB
};

/**
 * อ่านไฟล์เป็น base64 (data URL) สำหรับ fallback path เดิม
 * @param {File} file
 * @returns {Promise<string>}
 */
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('read file failed'));
    reader.readAsDataURL(file);
  });

/**
 * อัปโหลดไฟล์ขึ้น S3
 * 1) พยายามใช้ presigned URL (POST /auth/upload/presign) แล้ว PUT ไฟล์ตรงเข้า S3
 *    -> ไม่ผ่าน body ของ API จึงไม่ติด limit ของ proxy/express (เลี่ยง 413 ถาวร)
 * 2) ถ้า presign ใช้ไม่ได้ (เช่น endpoint ยังไม่ deploy หรือ bucket ยังไม่ตั้ง CORS)
 *    -> fallback ไปใช้ base64 ผ่าน POST /auth/upload ของเดิม (ใช้งานได้เหมือนเดิมทุกประการ)
 *
 * @param {File} file
 * @param {{ folder?: string }} options
 * @returns {Promise<{status: boolean, data?: {file_path, file_name, mime_type, file_size}, message?: string}>}
 */
export const UPLOAD = async (file, { folder } = {}) => {
  if (!file) return { status: false, message: 'No file' };

  // ---- 1) Presigned URL (ยิงตรงเข้า S3) ----
  try {
    const presign = await POST('auth/upload/presign', {
      file_name: file.name,
      mime_type: file.type || undefined,
      folder,
      file_size: file.size,
    });

    if (presign?.status === true && presign?.data?.upload_url) {
      const { upload_url, public_url, mime_type, content_disposition } = presign.data;
      const putHeaders = { 'Content-Type': mime_type || file.type || 'application/octet-stream' };
      // ต้องส่งค่าตรงกับที่ backend เซ็นไว้ใน presigned URL ไม่งั้น S3 จะตอบ 403 SignatureDoesNotMatch
      if (content_disposition) putHeaders['Content-Disposition'] = content_disposition;
      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: putHeaders,
        body: file,
      });

      if (putRes.ok) {
        return {
          status: true,
          data: {
            file_path: public_url,
            file_name: file.name || null,
            mime_type: mime_type || file.type || null,
            file_size: file.size ?? null,
          },
        };
      }
      // presign สำเร็จแต่ PUT พลาด (เช่น CORS) -> ตกไปใช้ fallback ด้านล่าง
    }
  } catch (e) {
    // เงียบไว้ แล้วไป fallback
  }

  // ---- 2) Fallback: base64 ผ่าน /auth/upload (ทางเดิม) ----
  try {
    const base64 = await fileToBase64(file);
    const res = await POST('auth/upload', { file: base64, file_name: file.name, folder });
    if (res?.status === true && res?.data?.path) {
      return {
        status: true,
        data: {
          file_path: res.data.path,
          file_name: file.name || res.data.name || null,
          mime_type: res.data.mime_type || file.type || null,
          file_size: res.data.size ?? file.size ?? null,
        },
      };
    }
    return { status: false, message: res?.message };
  } catch (e) {
    return { status: false, message: 'upload failed' };
  }
};

const api = {
  GET,
  POST,
  PUT,
  PATCH,
  DELETE,
  UPLOAD,
  ERROR,
  loading,
  showAlert,
  removeEmptyString,
  MAX_UPLOAD_SIZE,
};

export default api;
